import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, from, of } from 'rxjs';
import { AppService } from '../../application/app.service';
import { ConfigurationFn, ConfigurationObject, LssConfigurationEntity } from './configuration.datamodel';
import { ComponentWithConfigsObject } from '../componentsWithConfigs.datamodel';
import { DeploymentService } from '../../deployments/store/deployment.service';
import { LssDeploymentEntity, PossibleLssStatuses } from '../../deployments/store/deployment.datamodel';
import { OpenModalService } from '../../core/services/openModal.service';
import { LoginService } from '../../login/store/login.service';
import { ConfigurationService } from './configuration.service';
import { AppComponentsType } from '../../publish/component/store/component.datamodel';
import { FilterNoAccessConfigPipe } from '../../shared/pipes/filter-no-access-config.pipe';
import { ActorObject, ActorType } from '../../shared/datamodel';
import { EditConfigurationComponent } from '../edit-configuration/edit-configuration.component';
import { IsEntityDeployedPipe } from '../../deployments/pipes/is-entity-deployed.pipe';

import * as DeploymentActions from '../../deployments/store/deployment.actions';
import * as ConfigurationActions from './configuration.actions';
import * as AppActions from '../../application/app.actions';
import { SizeOverlap } from '../edit-configuration/edit-configuration.datamodel';

@Injectable()
export class ConfigurationEffects {
  /**
   * Reset the selection and collapse the details panel when the details are retrieved
   */
  resetSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.RetrieveDetailsCompleted),
      mergeMap(() => of(
        ConfigurationActions.ResetSelectionAndCollapsed(),
        ConfigurationActions.ResetDeployedConfigurations()
      ))
    )
  );

  /**
   * Populate the deployed configurations when the deployed entities are retrieved or updated
   */
  populateDeployedConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DeploymentActions.PopulateDeployedLssEntities,
        ConfigurationActions.UpdateScriptConfigurationCompleted,
        ConfigurationActions.UpdateScriptConfigurationPartiallyCompleted,
        ConfigurationActions.MassiveUpdateScriptConfigurationCompleted,
        ConfigurationActions.ConfigRedefinedCompleted,
        ConfigurationActions.ConfigDeletedCompleted
      ),
      withLatestFrom(
        this.appService.detailedApp$,
        this.deploymentService.entitiesMap$
      ),
      mergeMap(([_input, appDetails, entitiesMap]) => {
        const isEntityDeployed = new IsEntityDeployedPipe();
        const componentGuidToComponent = appDetails.cmpsWithConfigs.components.reduce(
          (map, component) => map.set(component.component.id, component),
          new Map<string, ComponentWithConfigsObject>()
        );
        const actorNameToConfigurationName = appDetails.deployedActors.reduce(
          (map, deployedActor) => map.set(deployedActor.actor.actorName, deployedActor.configurations),
          new Map<string, ConfigurationObject[]>()
        );
        const deployedEntities = Object.entries(entitiesMap)
          .filter(([_, entity]) => isEntityDeployed.transform(entity))
          .map(([actorName, _]) => [actorName, actorNameToConfigurationName.get(actorName)])
          .filter((entry): entry is [string, ConfigurationObject[]] => entry !== undefined);
        return of(ConfigurationActions.PopulateDeployedConfigurations({
          deployedActorsWithConfigurationIds: new Map(deployedEntities),
          deployedComponentsWithConfigurations: componentGuidToComponent
        }));
      })
    )
  );

  /**
   * Open the change configurations modal when the action is dispatched
   */
  openChangeConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationActions.OpenChangeConfigurationsModal),
      withLatestFrom(
        this.configurationService.selectedEntities$,
        this.appService.detailedApp$,
        this.appService.entitySelected$,
        this.configurationService.redefinedConfigurations$,
        this.deploymentService.selectedDeploymentLevelCategory$
      ),
      mergeMap(([
                  _,
                  selectedEntities,
                  app,
                  entitySelected,
                  redefinedConfigSuccess,
                  deploymentCategory
                ]) => {
          const selectedConfigurations = app.cmpsWithConfigs.components.reduce((selectedConfig, component) => {
            const defaultConfig = component.configurations
              .find(conf => ConfigurationFn.isDefaultConfig(conf));
            return {...selectedConfig, [defaultConfig?.componentId?.guid as string]: defaultConfig?.guid};
          }, {});
          const appComponentsType = this.appService.getAppComponentsType(app.components, app.applicationType);
          const filterNoAccess = new FilterNoAccessConfigPipe();
          const potentialConfigurationOverrides = selectedEntities.filter(
            entity => PossibleLssStatuses.TO_OVERRIDE.includes(entity.tag)
          );
          const MODAL = this.openModalService.openDialog(EditConfigurationComponent, {
            modal: {size: "xl"},
            data: {
              cmpsWithConfigs: filterNoAccess.transform(app.cmpsWithConfigs),
              entitySelected,
              selectedConfigurations,
              selectedEntities,
              redefinedConfigSuccess,
              deploymentCategory,
              deployedActors: app.deployedActors,
              editConfiguration: true,
              appId: app.id,
              orgId: entitySelected.label,
              displayDynamicForm: appComponentsType === AppComponentsType.FUNCTIONAL_PARAM,
              containsPotentialConfigurationOverrides:  SizeOverlap.between(selectedEntities,
                potentialConfigurationOverrides)
            }
          });

          return from<Promise<string>>(MODAL.result)
            .pipe(mergeMap(action => {
              if (action === 'CONFIRM') {
                return of(ConfigurationActions.ResetSelection());
              } else {
                return of();
              }
            }));
        }
      )
    )
  );

  /**
   * When OGU search is updated then expand the parent actors and select the actor that matches the search
   */
  oguSearchUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationActions.UpdateOguSearch),
      withLatestFrom(
        this.configurationService.selectOgusWithDeploymentsOrDeployedChildren$,
        this.deploymentService.entitiesMap$
      ),
      mergeMap(([{search}, entities, entitiesMap]) => {
        const searchingValueTrimmed = search.toUpperCase().trim();
        const searchResult = entities
          .map(entity => this.searchTreeBy(entity, entity => this.hasName(entity, searchingValueTrimmed)))
          .find(entity => entity !== undefined);
        if (searchResult) {
          const parents = this.getAllParents(entitiesMap, searchResult.parent);
          return of(
            ConfigurationActions.ResetSelectionAndCollapsed(),
            ConfigurationActions.ExpandParentActor({actors: parents}),
            ConfigurationActions.SelectParentActor({activeParent: searchResult.actor}),
          );
        }
        return EMPTY;
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly appService: AppService,
    private readonly openModalService: OpenModalService,
    private readonly loginService: LoginService,
    private readonly configurationService: ConfigurationService,
    private readonly deploymentService: DeploymentService
  ) {
  }

  /**
   * Get all the parents of an actor in a flattened array
   * @param entitiesMap the map of the entities
   * @param actor the actor to get the parents from
   */
  private getAllParents(entitiesMap: {
    [p: string]: LssDeploymentEntity
  }, actor: ActorObject | undefined): ActorObject[] {
    if (actor === undefined) {
      return [];
    }
    return [
      actor,
      ...this.getAllParents(entitiesMap, entitiesMap[actor.actorName].parent)
    ];
  }

  /**
   * Search for an entity in the tree that matches a condition
   * @param entity the entity to start the search from
   * @param condition the condition to satisfy
   */
  private searchTreeBy(entity: LssConfigurationEntity, condition: (entity: LssConfigurationEntity) => boolean): LssConfigurationEntity | undefined {
    if (condition(entity)) {
      return entity;
    }
    if (entity.childrenTree.length > 0) {
      return entity.childrenTree
        .map(child => this.searchTreeBy(child, condition))
        .find(entity => entity !== undefined);
    }
    return undefined;
  }

  /**
   * Check if an entity has a specific name
   * @param entity the entity to check
   * @param name the name to check
   */
  private hasName(entity: LssConfigurationEntity, name: string): boolean {
    if (entity.actor.actorType === ActorType.ORGANISATION) {
      return name.toUpperCase() === entity.actor.actorName.toUpperCase();
    }
    if (entity.actor.actorType === ActorType.OGU_ORGANISATION) {
      return `${name}_${entity.parentOrg?.actorName}`.toUpperCase() === entity.actor.actorName.toUpperCase();
    }
    return false;
  }
}
